import { Typography } from "antd"
import React, { memo, useMemo } from "react"

import { SETTINGS_PERMISSIONS_COLUMNS } from "../constants/columns.ts"
import { getColKey } from "../helpers/utils.ts"
import type {
  ColumnKey,
  OnBulkChange,
  OnFeaturesToggle,
  PermissionsMode,
  SectionRecord
} from "../types.ts"
import type {
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"
import { PermissionsActionRow } from "./PermissionsActionRow.tsx"
import { PermissionsColumnToggleRow } from "./PermissionsColumnToggleRow.tsx"
import { PermissionsFeaturesBlock } from "./PermissionsFeaturesBlock.tsx"

const { Text } = Typography

type SettingsPermissionsCardProps = {
  parentKey: keyof PermissionFeatures
  subKeys: SectionRecord
  onBulkChange: OnBulkChange
  onFeaturesToggle?: OnFeaturesToggle
  isUpdating?: boolean
  disabled?: boolean
  displayMap?: PermissionDisplayMap | null
} & PermissionsMode

export const PermissionsCard = memo(
  ({
    parentKey,
    subKeys,
    onBulkChange,
    onFeaturesToggle,
    isUpdating,
    disabled,
    displayMap,
    ...rest
  }: SettingsPermissionsCardProps) => {
    const { features, ...restSubKeys } = subKeys

    const modeProps: PermissionsMode = rest?.asForm
      ? { asForm: true }
      : { onItemChange: rest?.onItemChange }

    const columns = useMemo(() => {
      const present = new Set(
        Object.values(restSubKeys).flatMap((actions) =>
          Object.keys(actions)
            .map((action) => getColKey(action, actions))
            .filter((key): key is ColumnKey => key !== null)
        )
      )
      // A section whose rights are none of create/view/edit/delete — e-signature
      // is the first — has nothing to put in the grid, so it gets no columns.
      if (present.size === 0) return []
      return present.has("full_access")
        ? SETTINGS_PERMISSIONS_COLUMNS
        : SETTINGS_PERMISSIONS_COLUMNS.filter(
            (col) => col.key !== "full_access"
          )
    }, [restSubKeys])

    const gridTemplateColumns = `1fr repeat(${columns.length}, 100px)`

    return (
      <div>
        {columns.length > 0 && (
          <>
            {/* Column headers */}
            <div
              className={
                "py-1.5 px-4 grid border-b border-border bg-background"
              }
              style={{ gridTemplateColumns }}
            >
              <div />
              {columns.map((col) => (
                <Text
                  key={col.key}
                  type="secondary"
                  className={"text-center text-xs"}
                >
                  {col.label}
                </Text>
              ))}
            </div>

            <PermissionsColumnToggleRow
              parentKey={parentKey}
              restSubKeys={restSubKeys}
              columns={columns}
              gridTemplateColumns={gridTemplateColumns}
              onBulkChange={onBulkChange}
              isUpdating={isUpdating}
              disabled={disabled}
            />
          </>
        )}

        {Object.entries(restSubKeys).map(([subKey, actions]) => (
          <PermissionsActionRow
            key={subKey}
            parentKey={parentKey}
            subKey={subKey}
            actions={actions}
            columns={columns}
            gridTemplateColumns={gridTemplateColumns}
            isUpdating={isUpdating}
            displayMap={displayMap}
            {...modeProps}
          />
        ))}

        {features && (
          <PermissionsFeaturesBlock
            parentKey={parentKey}
            features={features}
            onFeaturesToggle={onFeaturesToggle}
            isUpdating={isUpdating}
            disabled={disabled}
            displayMap={displayMap}
            modeProps={modeProps}
          />
        )}
      </div>
    )
  }
)

PermissionsCard.displayName = "PermissionsCard"
